import { GUIDE_SKILLS, GUIDE_CARDS, GUIDE_CONCEPTS } from "./training-guides.js";
/* Generated by Challenges/build/export_guide_pdfs.py: the slugs whose guide is also
   published as a PDF, so the card can offer the download. A guide with no PDF simply
   has no link, which is why this is a separate generated file rather than a flag
   inside training-guides.js: the two exporters stay independent. */
import { GUIDE_PDFS } from "./training-guide-pdfs.js";
/*
=============================================
   TRAINING MATERIALS - CONTENT
=============================================
The data behind the student Training tab and the public /training.html page.
Both render from THIS file, so the two can never drift apart.

⚠️ WHO SEES WHAT (2026-10-08, "the access wall"). Only Skill Guides is PUBLIC
and lives in this file. Every other area is its own file in training-areas/ and
is loaded as a separate chunk, ONLY for a user whose audience is granted it
(GET /me/content-access; audiences = arizona.edu, ConCats, later other schools).
The worker refuses that chunk to anyone else, so the text never reaches them.
Design: CONTENT-ACCESS-AUDIENCES-DESIGN-2026-10-08.md at the repo root.

ADDING A NEW AREA: write training-areas/<slug>.js (copy an existing one), add
one line to PRIVATE_AREAS below and its slug to AREA_ORDER, then grant the slug
to an audience (node server/set-audiences.js). A public area goes in
PUBLIC_AREAS instead and needs no grant. Nothing in training.js or
training-render.js needs touching.

Deliberately NOT in the database. This is editorial content that changes when
someone records a video, not per-class state, and putting it in Postgres would
buy a migration and an admin screen for something a pull request already does
better. If it ever needs to differ per class, that is the moment to move it.

SECTION KINDS the renderer understands (see training-render.js):
   note   a single highlighted line, usually where to get the files
   steps  a numbered how-to-use row
   keys   a shortcut reference table, with an optional trailing tip
   day    a titled group of challenge cards
   warn   an amber troubleshooting block
   week   one week of practice, with every document that came out of it
   skillindex  a filterable A-Z of functions, each linking into a guide
   concepts    techniques rather than functions
   guides      one card per challenge, linking to its full written guide
Anything else is ignored rather than thrown, so a half-finished area in this
file degrades to a shorter page instead of a blank one.
*/

import { ICONS } from "./training-icons.js";

/* ── Skill Guides ──────────────────────────────────────────────────────────────
   David, 2026-08-30: "create a new section in the training area for students that
   has all of the guides... make an index of functions / techniques / concepts, so
   they can go through them, pick the one they are interested in, then click on it
   to get the full guide (and then find the assignment in their course)."

   The index is by FUNCTION, not by challenge, because a student who wants MID does
   not know MID lives in ch.text-parse and should not have to. Every link deep-links
   to the level that teaches it, not to the top of a ten-level guide.

   ⚠️ GENERATED DATA. GUIDE_SKILLS / GUIDE_CARDS / GUIDE_CONCEPTS come from
   Challenges/build/export_training_guides.py, which reads challenges.json. Rebuild
   a spec, re-run the exporter, and this page follows. Do not hand-edit the numbers. */
const SKILL_GUIDES = {
   slug: "skill-guides",
   kicker: "Reference",
   title: "Skill Guides",
   icon: ICONS.guide,
   summary: "Every function we drill, with a written guide for each, on the page or as a PDF. Find what you want to learn, read it, then do the challenge in your class.",
   stats: [GUIDE_SKILLS.length + " functions", GUIDE_CARDS.length + " guides", "always open"],
   intro: "This does not expire and it is not tied to a week. Look up a function, read how it works and where it stalls people, then find the matching challenge in your class list and actually do it. Reading is not practice.",
   sections: [
      {
         kind: "note",
         html: "<p><strong>How to use this.</strong> Search for a function, or browse the techniques below if you do not yet know what you are missing. Each guide covers <em>every</em> level of its challenge, including the last three that the workbook deliberately does not show you, so treat it as the thing you read <em>after</em> you have had a real go.</p>",
      },
      {
         kind: "skillindex",
         title: "Find a function",
         placeholder: "Type a function, or what you want to do: count, look up, text\u2026",
         html: "<p>Marked <span class=\"tr-skill-basic\">basic</span> means everyday Excel rather than a topic of its own. Everything else has a challenge built around it.</p>",
         items: GUIDE_SKILLS.map((s) => ({
            skill: s.skill,
            gloss: s.gloss,
            core: s.core,
            slug: s.slug,
            href: "assets/guide-" + s.slug + ".html#level-" + s.level,
            where: "Level " + s.level + ", " + s.levelName,
         })),
      },
      {
         kind: "concepts",
         title: "Techniques worth knowing by name",
         html: "<p>These are not functions, they are moves. They transfer between cases, which is why they are worth learning as ideas rather than as formulas.</p>",
         items: GUIDE_CONCEPTS.map((c) => ({
            title: c.title,
            text: c.text,
            href: "assets/guide-" + c.slug + ".html#level-" + c.level,
         })),
      },
      {
         kind: "guides",
         title: "All " + GUIDE_CARDS.length + " guides",
         html: "<p>Roughly easiest first. The one line under each title says whether you can start it cold, and it is derived from the formulas rather than guessed.</p>",
         items: GUIDE_CARDS.map((c) => ({
            title: c.title,
            tier: c.tier,
            open: c.open,
            verdict: c.verdict,
            teaches: c.teaches,
            questions: c.questions,
            points: c.points,
            levelCount: c.levels.length,
            href: "assets/guide-" + c.slug + ".html",
            pdfHref: GUIDE_PDFS.includes(c.slug) ? "assets/guide-" + c.slug + ".pdf" : "",
         })),
      },
   ],
};

/* ---------------------------------------------------------------------------
   THE REGISTRY (2026-10-08)

   PUBLIC_AREAS ship in every bundle and render for anyone, signed in or not.
   PRIVATE_AREAS are loaders, one per area, each its own chunk under
   /private/training/. The magic comment NAMES that chunk, and the worker reads
   the slug back out of the path, so the chunk name MUST be
   "private/training/<slug>" with <slug> exactly the area's slug.

   AREA_ORDER is the order on screen, public and private mixed.
--------------------------------------------------------------------------- */

export const PUBLIC_AREAS = [SKILL_GUIDES];

export const PRIVATE_AREAS = {
   "speed-challenges": () => import(/* webpackChunkName: "private/training/speed-challenges" */ "./training-areas/speed-challenges.js"),
   "practice-notes": () => import(/* webpackChunkName: "private/training/practice-notes" */ "./training-areas/practice-notes.js"),
   "lambda-library": () => import(/* webpackChunkName: "private/training/lambda-library" */ "./training-areas/lambda-library.js"),
   "case-library": () => import(/* webpackChunkName: "private/training/case-library" */ "./training-areas/case-library.js"),
};

export const AREA_ORDER = ["speed-challenges", "practice-notes", "skill-guides", "lambda-library", "case-library"];

/*
The areas this user may see, in screen order.

`access` is the body of GET /me/content-access, or null when the caller is not
signed in (or the API could not be reached). `all: true` is a professor or
admin. A private area that fails to load (the worker refused it, or a network
blip) is DROPPED, never thrown: the grid renders whatever did load, and the
public areas always do.
*/
export async function loadTrainingAreas(access) {
   const granted = access && access.all
      ? Object.keys(PRIVATE_AREAS)
      : (access && Array.isArray(access.areas) ? access.areas : []).filter((s) => PRIVATE_AREAS[s]);

   const loaded = await Promise.all(granted.map(async (slug) => {
      try {
         const mod = await PRIVATE_AREAS[slug]();
         return mod.default || null;
      } catch (err) {
         console.warn(`Training area ${slug} did not load:`, err && err.message);
         return null;
      }
   }));

   const all = [...PUBLIC_AREAS, ...loaded.filter(Boolean)];
   const rank = (a) => {
      const i = AREA_ORDER.indexOf(a.slug);
      return i === -1 ? AREA_ORDER.length : i;
   };
   return all.sort((a, b) => rank(a) - rank(b));
}

/* True when this visitor is seeing less than everything there is, which is
   what the "sign in for more" note keys off. */
export function hasHiddenAreas(access) {
   if (access && access.all) return false;
   const mine = new Set(access && Array.isArray(access.areas) ? access.areas : []);
   return Object.keys(PRIVATE_AREAS).some((s) => !mine.has(s));
}

export { ICONS };
